import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Icon, useSnackbar } from '@web-chat-core/ui-kit';
import { useTranslation } from 'react-i18next';
import { Participant } from 'livekit-client';
import { FEATURES } from 'consts';
import { useRaiseHand } from 'features/RaiseHand/hooks/useRaiseHand';
import { useRaisedHandParticipants } from 'features/RaiseHand/hooks/useRaisedHandParticipants';
import { participantListOrderSort } from 'useCase';
import { isFeatureOn } from 'useCase/featureFlags/featureFlagsApi';
import { useWaitingRoom } from 'features/AccessRequests/hooks/useWaitingRoom';
import { useWaitingRoomActions } from 'features/AccessRequests/hooks/useWaitingRoomActions';
import { WaitingUserAvatar } from 'features/AccessRequests/components/WaitingUserAvatar/WaitingUserAvatar';
import { getWaitingUserDisplayName } from 'features/AccessRequests/utils/getWaitingUserDisplayName';
import { ParticipantListItem } from './ParticipantListItem';
import { RaisedHandListItem } from './RaisedHandListItem';
import {
    List,
    ListItemUserInfo,
    ParticipantsDivider,
    ParticipantsListInner,
    ParticipantsSection,
    ParticipantsSectionDivider,
    ParticipantsSectionHeader,
    WaitingSection,
    WaitingSectionAction,
    WaitingSectionHeader,
    WaitingSectionTitle,
    WaitingUserActionButton,
    WaitingUserActions,
    WaitingUserItem,
    WaitingUserMain,
    WaitingUserName,
    WaitingUsersList,
    WaitingUserSub,
} from './view';

interface IProps {
    participants: Participant[];
}

export const ParticipantList: React.FC<IProps> = observer(({ participants }) => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { checkIsHandRaised } = useRaiseHand();
    const { waitingUsers, isCanManageWaitingUsers } = useWaitingRoom();
    const { approveWaitingUser, approveAllWaitingUsers } = useWaitingRoomActions();
    const sortedParticipants = participantListOrderSort(participants, checkIsHandRaised);
    const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
    const [isApproveAllPending, setIsApproveAllPending] = useState(false);
    const [isWaitingCollapsed, setIsWaitingCollapsed] = useState(false);
    const [isRaisedHandsCollapsed, setIsRaisedHandsCollapsed] = useState(false);
    const [isParticipantsCollapsed, setIsParticipantsCollapsed] = useState(false);

    const sortedWaitingUsers = useMemo(
        () => [...waitingUsers].sort((a, b) => a.waitingSince - b.waitingSince),
        [waitingUsers],
    );

    const isRaisedHandsListEnabled = isFeatureOn(FEATURES.LOCAL_VCS_RAISED_HANDS_LIST_ENABLED);
    const raisedHandParticipants = useRaisedHandParticipants(participants);

    const isCanCollapseParticipants = isCanManageWaitingUsers && sortedWaitingUsers.length > 0;
    const isWaitingUsersState = isCanManageWaitingUsers && sortedWaitingUsers.length > 0;
    // Отдельная секция поднятых рук (для Tango / IS_T выключена — руки только в общем списке)
    const isRaisedHandsState = isRaisedHandsListEnabled && raisedHandParticipants.length > 0;
    const showParticipantsHeader = isWaitingUsersState || isRaisedHandsState;
    const isParticipantsCollapsible = isCanCollapseParticipants || isRaisedHandsState;

    const handleWaitingUserAction = async (waitingListId: string, operation: 'APPROVE' | 'REJECT') => {
        setPendingIds((prev) => new Set(prev).add(waitingListId));
        try {
            const result = await approveWaitingUser([waitingListId], operation);
            if (result && 'error' in result) {
                showSnackbar(t('web_videoconference_error'));
            }
        } finally {
            setPendingIds((prev) => {
                const next = new Set(prev);
                next.delete(waitingListId);
                return next;
            });
        }
    };

    const handleApproveAll = async () => {
        setIsApproveAllPending(true);
        try {
            const result = await approveAllWaitingUsers();
            if (result && 'error' in result) {
                showSnackbar(t('web_videoconference_error'));
            }
        } finally {
            setIsApproveAllPending(false);
        }
    };

    return (
        <List>
            {isWaitingUsersState && (
                <WaitingSection>
                    <WaitingSectionHeader
                        type="button"
                        onClick={() => setIsWaitingCollapsed((v) => !v)}
                        aria-expanded={!isWaitingCollapsed}
                    >
                        <WaitingSectionTitle>
                            {t('vks_request_waiting_with_count', { 0: sortedWaitingUsers.length })}
                        </WaitingSectionTitle>
                        <Icon icon={isWaitingCollapsed ? 'icDown16' : 'icUp16'} size={16} />
                    </WaitingSectionHeader>
                    {!isWaitingCollapsed && (
                        <>
                            <ParticipantsDivider />
                            {sortedWaitingUsers.length > 1 && (
                                <WaitingSectionAction
                                    type="button"
                                    onClick={handleApproveAll}
                                    disabled={isApproveAllPending}
                                >
                                    {t('vks_request_allow_connection_for_all')}
                                </WaitingSectionAction>
                            )}
                            <WaitingUsersList>
                                {sortedWaitingUsers.map((user) => {
                                    const displayName = getWaitingUserDisplayName(user);
                                    const isGuest = !user.userId;

                                    return (
                                        <WaitingUserItem key={user.waitingListId}>
                                            <ListItemUserInfo>
                                                <WaitingUserAvatar user={user} />
                                                <WaitingUserMain>
                                                    <WaitingUserName>{displayName}</WaitingUserName>
                                                    {isGuest && (
                                                        <WaitingUserSub>{t('vks_request_guest')}</WaitingUserSub>
                                                    )}
                                                </WaitingUserMain>
                                            </ListItemUserInfo>
                                            <WaitingUserActions>
                                                <WaitingUserActionButton
                                                    type="button"
                                                    $kind="approve"
                                                    onClick={() =>
                                                        handleWaitingUserAction(user.waitingListId, 'APPROVE')
                                                    }
                                                    disabled={pendingIds.has(user.waitingListId) || isApproveAllPending}
                                                >
                                                    <Icon icon="icDone" colorVariant="colored" size={24} />
                                                </WaitingUserActionButton>
                                                <WaitingUserActionButton
                                                    type="button"
                                                    $kind="reject"
                                                    onClick={() =>
                                                        handleWaitingUserAction(user.waitingListId, 'REJECT')
                                                    }
                                                    disabled={pendingIds.has(user.waitingListId) || isApproveAllPending}
                                                >
                                                    <Icon icon="icClose16" size={16} />
                                                </WaitingUserActionButton>
                                            </WaitingUserActions>
                                        </WaitingUserItem>
                                    );
                                })}
                            </WaitingUsersList>
                        </>
                    )}
                </WaitingSection>
            )}
            {isRaisedHandsState && (
                <WaitingSection>
                    <WaitingSectionHeader
                        type="button"
                        onClick={() => setIsRaisedHandsCollapsed((v) => !v)}
                        aria-expanded={!isRaisedHandsCollapsed}
                    >
                        <WaitingSectionTitle>
                            {t('vks_request_raised_hands_count', { 0: raisedHandParticipants.length })}
                        </WaitingSectionTitle>
                        <Icon icon={isRaisedHandsCollapsed ? 'icDown16' : 'icUp16'} size={16} />
                    </WaitingSectionHeader>
                    {!isRaisedHandsCollapsed && (
                        <>
                            <ParticipantsDivider />
                            <WaitingUsersList>
                                {raisedHandParticipants.map((p) => (
                                    <RaisedHandListItem key={p.sid} participant={p} />
                                ))}
                            </WaitingUsersList>
                        </>
                    )}
                </WaitingSection>
            )}
            <ParticipantsSection>
                {showParticipantsHeader && (
                    <ParticipantsSectionHeader
                        as={isParticipantsCollapsible ? 'button' : 'div'}
                        type={isParticipantsCollapsible ? 'button' : undefined}
                        onClick={isParticipantsCollapsible ? () => setIsParticipantsCollapsed((v) => !v) : undefined}
                        aria-expanded={isParticipantsCollapsible ? !isParticipantsCollapsed : undefined}
                    >
                        {t('vks_request_participants')}
                        {isParticipantsCollapsible && (
                            <Icon icon={isParticipantsCollapsed ? 'icDown16' : 'icUp16'} size={16} />
                        )}
                    </ParticipantsSectionHeader>
                )}

                {(!isParticipantsCollapsible || !isParticipantsCollapsed) && (
                    <>
                        {showParticipantsHeader && <ParticipantsSectionDivider />}
                        <ParticipantsListInner>
                            {sortedParticipants.map((participant) => (
                                <ParticipantListItem key={participant.sid} participant={participant} />
                            ))}
                        </ParticipantsListInner>
                    </>
                )}
            </ParticipantsSection>
        </List>
    );
});
